import { useMemo, useState } from 'react';

import type {
  SkillInvocationDetail as SkillInvocationDetailData,
  SkillInvocationTraceBlock,
} from '../../lib/api-client';

import { StatusBadge, formatDuration, relativeTime } from './skill-invocation-helpers';

interface Props {
  detail: SkillInvocationDetailData | null;
  status: 'idle' | 'loading' | 'success' | 'error';
  error?: string | undefined;
}

/**
 * Right pane of the Skill Execution History tab. Three vertical sections:
 *   1. Header — status, user, conversation, timing.
 *   2. Summary — top-level input/output as JSON.
 *   3. Trace — nested tool_use/tool_result cards, indented by depth derived
 *      from parent_tool_use_id.
 */
export function SkillExecutionDetail({ detail, status, error }: Props) {
  if (status === 'loading' && !detail) return <Centered>Loading execution…</Centered>;
  if (status === 'error') return <Centered error>{error ?? 'Failed to load execution.'}</Centered>;
  if (!detail) {
    return <Centered>Select an execution from the left to inspect it.</Centered>;
  }

  return (
    <div
      style={{
        height: '100%',
        minHeight: 0,
        overflowY: 'auto',
        background: 'var(--bg)',
      }}
    >
      <Header detail={detail} />
      <Summary detail={detail} />
      <Trace blocks={detail.trace} skillToolUseRootName="Skill" />
    </div>
  );
}

function Header({ detail }: { detail: SkillInvocationDetailData }) {
  return (
    <div
      style={{
        padding: '16px 24px',
        borderBottom: '1px solid var(--border)',
        background: 'var(--surface)',
      }}
    >
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: 10,
          marginBottom: 8,
          flexWrap: 'wrap',
        }}
      >
        <StatusBadge status={detail.status} />
        <span style={{ fontSize: 13, fontWeight: 600, color: 'var(--text)' }}>
          {detail.skill_id}
        </span>
        <span style={{ fontSize: 12, color: 'var(--text-mute)' }}>
          by {detail.user_name ?? detail.user_email ?? detail.user_id}
        </span>
        {detail.conversation_title && (
          <span style={{ fontSize: 12, color: 'var(--text-mute)' }}>
            · in “{detail.conversation_title}”
          </span>
        )}
      </div>
      <Meta detail={detail} />
    </div>
  );
}

function Meta({ detail }: { detail: SkillInvocationDetailData }) {
  return (
    <div style={{ display: 'flex', gap: 24, fontSize: 11, color: 'var(--text-mute)', flexWrap: 'wrap' }}>
      <MetaItem label="Started" value={relativeTime(detail.started_at)} />
      <MetaItem label="Completed" value={detail.completed_at ? relativeTime(detail.completed_at) : '—'} />
      <MetaItem label="Duration" value={formatDuration(detail.duration_ms)} />
      <MetaItem label="Conversation" value={detail.conversation_id} mono />
      <MetaItem label="Invocation" value={detail.id} mono />
    </div>
  );
}

function MetaItem({ label, value, mono }: { label: string; value: string; mono?: boolean }) {
  return (
    <div>
      <div style={{ textTransform: 'uppercase', letterSpacing: 0.4, fontWeight: 600 }}>{label}</div>
      <div
        style={{
          color: 'var(--text)',
          fontFamily: mono ? 'ui-monospace, SFMono-Regular, Menlo, monospace' : 'inherit',
          fontSize: 11,
        }}
      >
        {value}
      </div>
    </div>
  );
}

function Summary({ detail }: { detail: SkillInvocationDetailData }) {
  return (
    <div style={{ padding: '16px 24px', borderBottom: '1px solid var(--border)' }}>
      <SectionLabel>Summary</SectionLabel>
      <div style={{ display: 'grid', gap: 12 }}>
        <JsonBlock title="Input" value={detail.input} />
        {detail.error_text ? (
          <JsonBlock title="Error" value={detail.error_text} variant="error" />
        ) : (
          <JsonBlock title="Output" value={detail.output} />
        )}
      </div>
    </div>
  );
}

function JsonBlock({
  title,
  value,
  variant,
}: {
  title: string;
  value: unknown;
  variant?: 'error';
}) {
  const pretty = useMemo(() => {
    if (value == null) return '—';
    if (typeof value === 'string') return value;
    try {
      return JSON.stringify(value, null, 2);
    } catch {
      return String(value);
    }
  }, [value]);

  return (
    <div>
      <div
        style={{
          fontSize: 11,
          color: 'var(--text-mute)',
          fontWeight: 600,
          letterSpacing: 0.4,
          textTransform: 'uppercase',
          marginBottom: 6,
        }}
      >
        {title}
      </div>
      <pre
        style={{
          margin: 0,
          padding: 12,
          background: 'var(--bg-alt)',
          border: `1px solid ${variant === 'error' ? 'var(--kind-failure, #cf222e)' : 'var(--border)'}`,
          borderRadius: 6,
          fontSize: 11,
          fontFamily: 'ui-monospace, SFMono-Regular, Menlo, monospace',
          color: variant === 'error' ? 'var(--kind-failure, #cf222e)' : 'var(--text)',
          whiteSpace: 'pre-wrap',
          wordBreak: 'break-word',
          maxHeight: 300,
          overflowY: 'auto',
        }}
      >
        {pretty}
      </pre>
    </div>
  );
}

function SectionLabel({ children }: { children: React.ReactNode }) {
  return (
    <div
      style={{
        fontSize: 11,
        fontWeight: 600,
        letterSpacing: 0.5,
        color: 'var(--text-mute)',
        textTransform: 'uppercase',
        marginBottom: 12,
      }}
    >
      {children}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Trace rendering — flat blocks → depth-aware tree.
// ---------------------------------------------------------------------------

interface TraceNode {
  block: SkillInvocationTraceBlock;
  /** 0 = root (the Skill tool_use itself), 1+ = nested under it. */
  depth: number;
}

function buildTraceLevels(
  blocks: SkillInvocationTraceBlock[],
  rootName: string,
): TraceNode[] {
  // Walk the flat trace once: maintain a map from tool_use_id → depth.
  // The Skill block (block.name === rootName) is depth 0. Subsequent
  // tool_use blocks inherit depth = parent_depth + 1 via parent_tool_use_id.
  // tool_result blocks render at the same depth as the tool_use they answer.
  const depthByToolUseId = new Map<string, number>();
  const out: TraceNode[] = [];

  for (const block of blocks) {
    let depth = 0;
    if (block.type === 'tool_use') {
      if (block.name === rootName) {
        depth = 0;
        if (typeof block.id === 'string') depthByToolUseId.set(block.id, 0);
      } else {
        const parentId = (block as any).parent_tool_use_id as string | null | undefined;
        const parentDepth =
          parentId && depthByToolUseId.has(parentId) ? depthByToolUseId.get(parentId)! : 1;
        depth = parentDepth + 1;
        if (typeof block.id === 'string') depthByToolUseId.set(block.id, depth);
      }
    } else if (block.type === 'tool_result') {
      const tuid = typeof block.tool_use_id === 'string' ? block.tool_use_id : undefined;
      depth = tuid && depthByToolUseId.has(tuid) ? depthByToolUseId.get(tuid)! : 1;
    } else {
      depth = 1;
    }
    out.push({ block, depth });
  }
  return out;
}

function Trace({
  blocks,
  skillToolUseRootName,
}: {
  blocks: SkillInvocationTraceBlock[];
  skillToolUseRootName: string;
}) {
  const nodes = useMemo(
    () => buildTraceLevels(blocks, skillToolUseRootName),
    [blocks, skillToolUseRootName],
  );

  return (
    <div style={{ padding: '16px 24px' }}>
      <SectionLabel>Trace ({blocks.length})</SectionLabel>
      {nodes.length === 0 ? (
        <div style={{ fontSize: 12, color: 'var(--text-mute)' }}>No trace blocks captured.</div>
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
          {nodes.map((n, i) => (
            <TraceCard
              key={`${typeof n.block.id === 'string' ? n.block.id : ''}-${i}`}
              node={n}
              defaultOpen={i < 4}
            />
          ))}
        </div>
      )}
    </div>
  );
}

function TraceCard({ node, defaultOpen }: { node: TraceNode; defaultOpen: boolean }) {
  const [open, setOpen] = useState(defaultOpen);
  const { block, depth } = node;
  const indent = Math.min(depth, 8) * 16;

  const title = (() => {
    if (block.type === 'tool_use') {
      return `▶ ${block.name ?? 'tool_use'}${typeof block.id === 'string' ? `  ·  ${block.id.slice(0, 14)}` : ''}`;
    }
    if (block.type === 'tool_result') {
      const err = block.is_error ? ' (error)' : '';
      const tuid = typeof block.tool_use_id === 'string' ? block.tool_use_id.slice(0, 14) : '';
      return `◀ tool_result${err}${tuid ? `  ·  for ${tuid}` : ''}`;
    }
    if (block.type === 'text') {
      return '✎ text';
    }
    return `· ${String(block.type ?? 'block')}`;
  })();

  const borderColor =
    block.type === 'tool_result' && block.is_error
      ? 'var(--kind-failure, #cf222e)'
      : block.type === 'tool_use'
        ? 'var(--accent)'
        : 'var(--border)';

  const json = useMemo(() => {
    try {
      return JSON.stringify(block, null, 2);
    } catch {
      return String(block);
    }
  }, [block]);

  return (
    <div style={{ marginLeft: indent }}>
      <div
        style={{
          border: '1px solid var(--border)',
          borderLeft: `3px solid ${borderColor}`,
          borderRadius: 6,
          background: 'var(--surface)',
          overflow: 'hidden',
        }}
      >
        <button
          type="button"
          onClick={() => setOpen((v) => !v)}
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: 8,
            width: '100%',
            padding: '8px 12px',
            background: 'transparent',
            border: 'none',
            cursor: 'pointer',
            textAlign: 'left',
            color: 'var(--text)',
            fontFamily: 'ui-monospace, SFMono-Regular, Menlo, monospace',
            fontSize: 11,
          }}
        >
          <span style={{ color: 'var(--text-mute)', width: 12 }}>{open ? '▾' : '▸'}</span>
          <span>{title}</span>
        </button>
        {open && (
          <pre
            style={{
              margin: 0,
              padding: '8px 12px 12px 32px',
              background: 'var(--bg-alt)',
              fontSize: 11,
              fontFamily: 'ui-monospace, SFMono-Regular, Menlo, monospace',
              color: 'var(--text)',
              whiteSpace: 'pre-wrap',
              wordBreak: 'break-word',
              maxHeight: 320,
              overflowY: 'auto',
              borderTop: '1px solid var(--border)',
            }}
          >
            {json}
          </pre>
        )}
      </div>
    </div>
  );
}

function Centered({ children, error }: { children: React.ReactNode; error?: boolean }) {
  return (
    <div
      style={{
        height: '100%',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        color: error ? 'var(--kind-failure)' : 'var(--text-mute)',
        fontSize: 13,
        padding: 24,
        textAlign: 'center',
      }}
    >
      {children}
    </div>
  );
}

// Exported for unit testing.
export const __testing = { buildTraceLevels };
